import {RevaValuationPurpose, VALUATION_PURPOSES} from "@reva/shared/valuation-purpose";

/** Pfad der Handbuch-Seite; der QR-Code im Handbuch-PDF zeigt hierher (reva-manual `\handbuchurl`). */
export const MANUAL_ROUTE_PATH = "/handbuch";

/**
 * Ob `pathname` die Handbuch-Seite ist (#19302). Dort lädt REVA nach der Anmeldung keine statischen Daten (Spec,
 * GeoServer, Freischaltungen) und baut keinen Router – siehe `AppContainer` und `loginThunk`.
 */
export function isManualPath(pathname: string): boolean {
    return pathname === MANUAL_ROUTE_PATH || pathname === `${MANUAL_ROUTE_PATH}/`;
}

/** Ein REVA-Anwenderhandbuch (#19302). */
export interface RevaManual {
    /**
     * i18n-Key der Beschriftung im Fußzeilen-Menü „Dokumentation“ und des Download-Knopfs auf `/handbuch`; Muster
     * „Anwenderhandbuch <Zweck>“ wie auf dem PDF-Deckblatt.
     */
    labelKey: string;
    /** Dateiname der deutschen Fassung in `avm_deployed.avm_documentation`, falls die AVM-API keinen mitschickt. */
    fallbackFilename: string;
    /** `type` des Handbuchs in der Dokumentationsliste der AVM-API, für das Stand-Datum. */
    documentationType: AvmDocumentationType;
}

/**
 * Anwenderhandbücher je Zweck (#19302). Ein Zweck ohne Eintrag hat (noch) kein Handbuch: kein Menüpunkt in der
 * Fußzeile, und die Route `/handbuch` fällt auf einen Zweck mit Handbuch zurück.
 */
export const REVA_MANUALS: Partial<Record<RevaValuationPurpose, RevaManual>> = {
    VALUATION: {
        labelKey: "footer.manualValuation",
        fallbackFilename: "VALUE_REVA_anwenderhandbuch_bewertung.pdf",
        documentationType: "reva_manual_valuation",
    },
};

/**
 * Zweck, dessen Handbuch die Route `/handbuch` lädt (#19302): der in der URL angefragte Zweck, wenn es dafür ein
 * Handbuch gibt; sonst der erste Zweck mit Handbuch, mit dem der User anlegen darf; sonst Bewertung. Bewusst keine
 * Rechteprüfung auf den angefragten Zweck – das Handbuch ist für alle angemeldeten User lesbar.
 * @param requested Wert des Query-Parameters `purpose` (Groß-/Kleinschreibung egal), oder null
 * @param creatable Zwecke, mit denen der User anlegen darf
 */
export function resolveManualPurpose(
    requested: string | null,
    creatable: readonly RevaValuationPurpose[],
): RevaValuationPurpose {
    const normalized = requested?.trim().toUpperCase();
    const requestedPurpose = VALUATION_PURPOSES.find((p) => p === normalized);
    if (requestedPurpose && REVA_MANUALS[requestedPurpose]) return requestedPurpose;
    return creatable.find((p) => REVA_MANUALS[p]) ?? RevaValuationPurpose.VALUATION;
}

/** Dokumentarten in `avm_deployed.avm_documentation`, wie sie die Dokumentationsliste der AVM-API liefert (#19302). */
export type AvmDocumentationType =
    | "reva_manual_valuation"
    | "reva_manual_research"
    | "avm_documentation"
    | "release"
    | "parameters"
    | "validation_report"
    | "documentation";

/** Eine Zeile aus `GET /v1/documentation/list` der AVM-API (#19302); nur die Felder, die REVA auswertet. */
export interface AvmDocumentationEntry {
    type: string;
    language: string | null;
    /** Endpunkt der Methodenbeschreibung, z. B. `COMPARATIVE_VALUE` oder `LOCATION_INFORMATION_TIMELINES`. */
    endpoint: string | null;
    /** Upload-Datum als ISO-8601 mit Offset. */
    updatedAt: string | null;
}

/** Welche Zeile der Dokumentationsliste zu einem Knopf gehört. */
export interface AvmDocumentationKey {
    type: AvmDocumentationType;
    /** Nur Zeilen mit diesem Endpunkt; ohne Angabe nur Zeilen ohne Endpunkt. */
    endpoint?: string;
    /** Nur Zeilen in dieser Sprache (Parameterübersicht); ohne Angabe bevorzugt die Oberflächensprache, sonst Deutsch. */
    language?: string;
}

const optionalString = (value: unknown): value is string | null | undefined =>
    value == null || typeof value === "string";

/**
 * Prüft die Antwort der Dokumentationsliste (#19302). Unerwartetes Format ergibt eine leere Liste, damit die
 * Handbuch-Seite dann stillschweigend ohne Stand-Datum bleibt; einzelne kaputte Zeilen fallen weg.
 * @param json Antwort der AVM-API
 */
export function parseDocumentationList(json: unknown): AvmDocumentationEntry[] {
    if (!Array.isArray(json)) return [];
    return json.flatMap((row: unknown) => {
        if (typeof row !== "object" || row === null) return [];
        const {type, language, endpoint, updatedAt} = row as Record<string, unknown>;
        if (typeof type !== "string" || !optionalString(language)) return [];
        if (!optionalString(endpoint) || !optionalString(updatedAt)) return [];
        return [{type, language: language ?? null, endpoint: endpoint ?? null, updatedAt: updatedAt ?? null}];
    });
}

/**
 * Stand-Datum (Upload-Datum) des Dokuments zu `key` aus der Dokumentationsliste (#19302), oder null.
 * @param entries geprüfte Dokumentationsliste
 * @param key Zuordnung des Knopfs
 * @param uiLanguage Oberflächensprache; bei mehreren Sprachfassungen gewinnt sie, sonst Deutsch
 */
export function documentationUpdatedAt(
    entries: readonly AvmDocumentationEntry[],
    key: AvmDocumentationKey,
    uiLanguage: string,
): string | null {
    const candidates = entries.filter(
        (e) =>
            e.type === key.type &&
            e.endpoint === (key.endpoint ?? null) &&
            (key.language === undefined || e.language === key.language),
    );
    const preferred = uiLanguage.toLowerCase().startsWith("en") ? "en" : "de";
    const match =
        candidates.find((e) => e.language === preferred) ??
        candidates.find((e) => e.language === "de") ??
        candidates[0];
    return match?.updatedAt ?? null;
}
